import { useAuth } from "@features/auth";
import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useNavigation } from "@core/context/NavigationContext";
import { Info, InfoIcon, RotateCcw } from "lucide-react";
import React from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@shared/ui/breadcrumb";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { useQueryClient } from "@tanstack/react-query";
export function useBaseComponent() {
  const [error, setError] = useState<Error | undefined>(undefined);
  const { user } = useAuth();
  const location = useLocation();
  const { navigationItems } = useNavigation();
  const platformUrl = import.meta.env.VITE_PLATFORM_URL;
  const urlParts = platformUrl?.split("//")[1]?.split(".") || [];
  const environment = urlParts[0]?.replace("platform-", "") || "production";

  // Function to get environment styles and label
  const getEnvironmentConfig = (env: string) => {
    switch (env) {
      case "sandbox":
        return {
          label:
            "You are in a sandbox environment designed for practice and learning. No real operational or production actions will take place here.",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-300",
          textColor: "text-yellow-700",
          iconColor: "text-yellow-500",
          show: true,
        };
      case "dev":
      case "development":
        return {
          label: "This is Development Environment",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-300",
          textColor: "text-blue-700",
          iconColor: "text-blue-500",
          show: true,
        };
      case "staging":
        return {
          label: "This is Staging Environment",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-300",
          textColor: "text-orange-700",
          iconColor: "text-orange-500",
          show: true,
        };
      case "new":
        return {
          label: "This is Pre-production Environment",
          bgColor: "bg-purple-50",
          borderColor: "border-purple-300",
          textColor: "text-purple-700",
          iconColor: "text-purple-500",
          show: true,
        };
      case "production":
      default:
        return {
          label: "This is Production Environment",
          bgColor: "bg-green-50",
          borderColor: "border-green-300",
          textColor: "text-green-700",
          iconColor: "text-green-500",
          show: false,
        };
    }
  };

  const envConfig = getEnvironmentConfig(environment);

  function renderError() {
    if (!error) return null;
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <h2 className="text-lg font-semibold text-red-800">Error</h2>
        <p className="mt-2 text-sm text-red-600">{error.message}</p>
      </div>
    );
  }

  function formatSegment(segment: string): string {
    // Replace hyphens with spaces → "user-profile" → "user profile"
    const withSpaces = segment.replace(/-/g, " ");

    // Capitalize each word → "user profile" → "User Profile"
    return withSpaces.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const queryClient = useQueryClient();

  // State to track last updated time
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const handleRefresh = () => {
    // Option 1: Invalidate everything (triggers refetch of all active queries)
    queryClient.invalidateQueries();

    // Update last updated time
    setLastUpdated(new Date());
  };

  function renderBreadcrumbs() {
    let pathSegments = location.pathname.split("/").filter(Boolean);

    // ✅ Remove segments that are purely numbers
    pathSegments = pathSegments.filter((segment) => isNaN(Number(segment)));

    // ✅ Limit to max 4 segments
    if (pathSegments.length > 4) {
      pathSegments = pathSegments.slice(0, 4);
    }

    const breadcrumbs = [];

    // ✅ Add home breadcrumb (read-only now)
    breadcrumbs.push(
      <BreadcrumbItem key="home">
        <BreadcrumbPage>Home</BreadcrumbPage>
      </BreadcrumbItem>
    );

    // ✅ Build read-only breadcrumbs
    pathSegments.forEach((segment) => {
      const label =
        (() => {
          const navItem = navigationItems
            ?.flatMap((nav) => nav.items)
            ?.find((item) => item?.link?.endsWith(segment));
          return navItem?.label;
        })() || formatSegment(segment);

      breadcrumbs.push(
        <BreadcrumbItem key={segment}>
          <BreadcrumbPage>{label}</BreadcrumbPage>
        </BreadcrumbItem>
      );
    });

    return (
      <div>
        {envConfig.show && (
          <div
            className={`flex items-center gap-2 border ${envConfig.borderColor} ${envConfig.bgColor} p-2 rounded`}
          >
            <InfoIcon className={`w-4 h-4 ${envConfig.iconColor}`} />
            <span className={envConfig.textColor}>{envConfig.label}</span>
          </div>
        )}

        <div className="flex items-center justify-between w-full mt-2">
          {/* ✅ Left: Breadcrumbs */}
          <div className="flex items-center gap-2">
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((item, index) => (
                  <React.Fragment key={index}>
                    {item}
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>

            {/* ✅ Breadcrumb Info tooltip */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Breadcrumbs are currently unavailable. Use side navigation.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* ✅ Right: Refresh + Info + Last Updated */}
          <div className="flex flex-col items-end gap-2">
            {/* Line 1 - Refresh button */}
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-2 py-2 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* Line 2 - Info + last updated */}
            <div className="flex items-center gap-1 text-gray-600 text-xs">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 cursor-pointer hover:text-gray-800 transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This shows the last time data was refreshed.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span>
                Last updated {lastUpdated && lastUpdated.toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return {
    error,
    setError,
    renderError,
    renderBreadcrumbs,
  };
}
